import { v4 as uuidv4 } from 'uuid';
import { NodeType } from '@amplify/amp-module';
import isUrl from '@/utils/url';
import { favIconUrl } from '@/constants';
import { DEFAULT_CONVERSATION_NAME } from '@/constants/amp';

const baseEdetailerUrl = process.env.BASE_EDETAILER_URL;

/**
 * Adds all updates to the given object in a recursive manner.
 * @param {object} obj - Initial object
 * @param {object} updates - Updates properties
 */
export const updateProperties = (obj = {}, updates = {}, replaceNested = false) => {
  Object.keys(updates).forEach((key) => {
    const item = updates[key];

    if (typeof item === 'object' && !Array.isArray(item) && item) {
      obj[key] = replaceNested ? {} : obj[key] || {};

      updateProperties(obj[key], item);
    } else {
      obj[key] = item;
    }
  });
};

/**
 * Based on a conversation finds the id of the previous conversation in the list.
 * @param {object} conversation - current conversation.
 * @param {object[]} conversations - list of all the conversations.
 * @returns
 */
export const findPreviousConversation = (conversation, conversations) => {
  const filteredConversations = objectToSortedArray(conversations).filter(
    (c) =>
      c.id === conversation.parentId || c.parentId === conversation.parentId || (!c.parentId && !conversation.parentId)
  );

  const index = filteredConversations.findIndex((c) => c.id === conversation.id);

  if (index) {
    return filteredConversations[index - 1].id;
  }

  return filteredConversations.find((c) => c.id !== conversation.id).id;
};

/**
 * Updates sortIndexes on the properties of an object.
 * @param {Object} object - object with properties to be updated.
 * @param {Object} startIndex - starting index of the properties.
 * @param {number} updateAmount - amount to update the indexes.
 */
export const updateIndexes = (object, startIndex, updateAmount) => {
  objectToSortedArray(object).forEach(({ id, sortIndex, nodeType }) => {
    if (sortIndex >= startIndex) {
      if (nodeType && [NodeType.buttonGroup, NodeType.question].includes(nodeType)) {
        object[id].subNodes.forEach((subNode) => {
          subNode.sortIndex = subNode.sortIndex + updateAmount;
        });
      }

      object[id].sortIndex = sortIndex + updateAmount;
    }
  });
};

/**
 * Inserts an element from the object and updates all sortIndexes.
 * @param {Object} object - The object where the element will be inserted as an element.
 * @param {Object} element - The element to inserted into the object as a property.
 */
export const insertElement = (object, element) => {
  let updateAmount = 1;

  if ([NodeType.buttonGroup, NodeType.question].includes(element.nodeType)) {
    updateAmount = updateAmount + element.subNodes.length;
  }

  if (!object[element.id] && element.sortIndex >= 0) {
    updateIndexes(object, element.sortIndex, updateAmount);
  }

  object[element.id] = element;
};

/**
 * Removes an element as a property from an object based in the id and updates all sortIndexes.
 * @param {Object} object - Object where the element property will be removed from.
 * @param {Object} element - Element to be removed from the object.
 */
export const removeElement = (object, element) => {
  let updateAmount = -1;

  if ([NodeType.buttonGroup, NodeType.question].includes(element.nodeType)) {
    updateAmount = updateAmount - element.subNodes.length;
  }

  if (element.sortIndex > 0) {
    updateIndexes(object, element.sortIndex, updateAmount);
  }

  delete object[element.id];
};

/**
 * Creates a conversation object.
 * @param {Object.name} [name] - Name of the conversation.
 * @param {Object.parentId} [parentId] - id of the parent conversation.
 * @param {Object.sortIndex} [sortIndex] -  sort index in a list.
 * @returns
 */
export const createConversationObject = ({ name = DEFAULT_CONVERSATION_NAME, parentId = null, sortIndex = 0 }) => ({
  id: uuidv4(),
  name,
  parentId,
  sortIndex,
  nodes: {},
  triggers: {},
});

/**
 * Default initialization state of the Chatbot menu
 */
export const defaultMenuState = {
  externalSectionTitle: '',
  external: {},
  internalSectionTitle: '',
  internal: {},
};

/**
 * Sorts elements based on a sortIndex.
 * @param {Object} a - first test element.
 * @param {Object} b - second test element.
 * @returns
 */
export const sortBySortIndex = (a, b) => a.sortIndex - b.sortIndex;

/**
 * Converts an objects properties to a sorted array based on sortIndexes.
 * @param {Object} obj - Object to be converted to a sorted array.
 * @returns {Array} - sorted array.
 */
export const objectToSortedArray = (obj) => Object.values(obj ?? {}).sort(sortBySortIndex);

/**
 * Assigns new sort indexes to all properties based on a depth first algorithm.
 * @param {Object} nodesObj - object with node properties
 * @returns
 */
export const updateNodeIndexes = (nodesObj) => {
  const orderedNodeArray = createOrderedNodeArray(Object.values(nodesObj));

  return orderedNodeArray.reduce((prev, curr, sortIndex) => {
    prev[curr.id] = { ...curr, sortIndex };

    return prev;
  }, {});
};

/**
 * Finds the next node in the array based on the parent id.
 * @param {Array} nodes - Array of all nodes.
 * @param {String} parentId - Parent id of the previous node.
 * @returns {Object} - returns the next node.
 */
export const findNextNode = (nodes, parentId) => nodes.find((node) => node.parentId?.endsWith(parentId));

/**
 * Finds the next nodes in the array based on the parent id.
 * @param {Array} nodes - Array of all nodes.
 * @param {String} parentId - Parent id of the previous node.
 * @returns {Array} - returns array of the next nodes.
 */
export const findNextNodes = (nodes, parentId) => nodes.filter((node) => node.parentId?.startsWith(parentId));

/**
 * Handles the case of a node with a single port.
 * @param {Array} originalArray - initial node array.
 * @param {Array} [orderedArray=[]] - ordered list of nodes.
 * @param {Object} node - Node object.
 * @returns - orderedArray.
 */
const handleNode = (originalArray, orderedArray, node) => {
  const childNode = findNextNode(originalArray, node.id);

  if (childNode) {
    return createOrderedNodeArray(originalArray, orderedArray, childNode);
  }

  return orderedArray;
};

/**
 * Handles the case of a condition node with a two ports.
 * @param {Array} originalArray - initial node array.
 * @param {Array} [orderedArray=[]] - ordered list of nodes.
 * @param {Object} node - Node object.
 * @returns - orderedArray.
 */
const handleConditionNodes = (originalArray, orderedArray, node) => {
  ['true', 'false'].forEach((condition) => {
    const childNode = findNextNode(originalArray, `${node.id}_port_${condition}`);

    if (childNode) {
      orderedArray = createOrderedNodeArray(originalArray, orderedArray, childNode);
    }
  });

  return orderedArray;
};

/**
 * Handles the case of sub nodes with individual ports.
 * @param {Array} originalArray - initial node array.
 * @param {Array} [orderedArray=[]] - ordered list of nodes.
 * @param {Object} node - Node object.
 * @returns - orderedArray.
 */
const handleSubNodes = (originalArray, orderedArray, node) => {
  const subNodes = objectToSortedArray(node.subNodes);

  subNodes.forEach((subNode) => {
    const childNode = findNextNode(originalArray, `${node.id}_port_${subNode.id}`);

    if (childNode) {
      orderedArray = createOrderedNodeArray(originalArray, orderedArray, childNode);
    }
  });

  return orderedArray;
};

/**
 * Returns an ordered array without any orphaned nodes.
 * @param {Array} originalArray - initial node array
 * @param {Array} [orderedArray=[]] - ordered list of nodes
 * @param {Object} node - Node object.
 * @returns - orderedArray.
 */
const createOrderedNodeArray = (originalArray, orderedArray = [], node = null) => {
  if (!node) {
    node = originalArray.find((n) => !n.parentId);
  }

  orderedArray.push(node);

  switch (node.type) {
    case NodeType.buttonGroup:
    case NodeType.question:
      return handleSubNodes(originalArray, orderedArray, node);
    case NodeType.condition:
      return handleConditionNodes(originalArray, orderedArray, node);
    default:
      return handleNode(originalArray, orderedArray, node);
  }
};

/**
 * Returns the truncated string by length
 * @param string
 * @param length
 * @returns {string|*}
 */
export const truncateString = (string, length) => (string.length > length ? `${string.slice(0, length)}...` : string);

export const generateDomainPassword = (length) => {
  let result = '';
  const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  const charactersLength = characters.length;
  let counter = 0;
  while (counter < length) {
    result += characters.charAt(Math.floor(Math.random() * charactersLength));
    counter += 1;
  }
  return result;
};

export const getURLFavicon = (url) => {
  try {
    return isUrl(url) && !url.includes('anthill') && !url.includes('ngrok')
      ? `https://www.google.com/s2/favicons?domain_url=${url}&sz=128`
      : favIconUrl;
  } catch (error) {
    return null;
  }
};

export const getURLTitle = (url) => {
  try {
    const hostname = new URL(url).hostname.replaceAll('.', ' ').split(' ');

    return hostname.reduce((name, current) => {
      if (current !== 'www') {
        name += ` ${current.charAt(0).toUpperCase() + current.slice(1)}`;
      }

      return name;
    }, []);
  } catch (e) {
    return '';
  }
};

export const getConversationThumbnail = (conversation, amp) => {
  if (!conversation?.slide) return '';
  const slide = encodeURIComponent(conversation.slide).replace(/^(v[^%\W]+)%2F/, (_, version) => `${version}/`);
  return `${baseEdetailerUrl}/${amp.organizationId}/${amp.id}/${slide}/thumb.png`;
};

export const getTrendValue = (current, comparedTo) => {
  const diff = current - comparedTo;
  if (comparedTo === 0)
    return current === comparedTo ? { percentage: 0, isDown: false } : { percentage: 100, isDown: false };
  return {
    isDown: diff < 0,
    percentage: Number.parseFloat(((Math.abs(diff) / Math.abs(comparedTo)) * 100).toFixed(2)),
  };
};
